import { cachedFetch } from './cachedFetch.js';
const DEFAULT_ERROR = 'Conference standings are temporarily unavailable.';

export async function getStandings({ signal, conference = '', force = false } = {}) {
  const response = await cachedFetch(`/api/standings${conference ? `?conference=${encodeURIComponent(conference)}` : ''}`, {
    headers: { Accept: 'application/json' },
    signal,
    force,
    maxAge: 30000,
    staleAge: 0,
  });
  const payload = await readJson(response);

  if (!response.ok) {
    throw new Error(payload?.message || DEFAULT_ERROR);
  }

  return {
    sport: String(payload?.sport || "Women's Volleyball").trim(),
    division: String(payload?.division || 'd1').trim(),
    title: String(payload?.title || 'Division I Conference Standings').trim(),
    updated: String(payload?.updated || '').trim(),
    season: integerOrNull(payload?.season),
    mode: String(payload?.mode || 'official-standings').trim(),
    notice: String(payload?.notice || '').trim(),
    conferenceRecordsAvailable: Boolean(payload?.conferenceRecordsAvailable),
    conferences: Array.isArray(payload?.conferences)
      ? payload.conferences.map(normalizeConference).filter(Boolean)
      : [],
    summary: normalizeSummary(payload?.summary),
    source: String(payload?.source || '').trim(),
  };
}

function normalizeConference(value) {
  const name = String(value?.name || '').trim();
  const slug = String(value?.slug || '').trim();
  const teams = Array.isArray(value?.teams)
    ? value.teams.map(normalizeStanding).filter(Boolean)
    : [];

  if (!name || !slug) {
    return null;
  }

  return {
    name,
    slug,
    teamCount: integerOrNull(value?.teamCount) || teams.length,
    teams,
    season: integerOrNull(value?.season),
    state: String(value?.state || 'unavailable'),
    notice: String(value?.notice || ''),
    source: String(value?.source || ''),
    sourceUrl: safeSourceUrl(value?.sourceUrl),
    fetchedAt: String(value?.fetchedAt || ''),
    stale: Boolean(value?.stale),
    rulesNote: String(value?.rulesNote || ''),
    rulesStatus: String(value?.rulesStatus || ''),
  };
}

function normalizeStanding(value) {
  const team = normalizeTeam(value?.team);
  const conference = {
    name: String(value?.conference?.name || '').trim(),
    slug: String(value?.conference?.slug || '').trim(),
  };

  if (!team.name || !team.slug || !conference.name || !conference.slug) {
    return null;
  }

  return {
    id: String(value?.id || `${conference.slug}:${team.slug}`).trim(),
    position: integerOrNull(value?.position),
    positionLabel: String(value?.positionLabel || '—'),
    tied: Boolean(value?.tied),
    division: String(value?.division || ''),
    divisionRecord: normalizeRecord(value?.divisionRecord),
    rankingBasis: String(value?.rankingBasis || 'conference'),
    seed: null,
    nationalRank: integerOrNull(value?.nationalRank),
    previousRank: integerOrNull(value?.previousRank),
    previousRankLabel: String(value?.previousRankLabel || '').trim(),
    leader: Boolean(value?.leader),
    conference,
    team,
    conferenceRecord: normalizeRecord(value?.conferenceRecord),
    conferencePct: numberOrNull(value?.conferencePct),
    conferencePctLabel: String(value?.conferencePctLabel || '').trim(),
    overallRecord: normalizeRecord(value?.overallRecord),
    overallPct: numberOrNull(value?.overallPct),
    overallPctLabel: String(value?.overallPctLabel || '').trim(),
    streak: String(value?.streak || '').trim(),
  };
}

function safeSourceUrl(value) {
  try { const url = new URL(value); return url.protocol === 'https:' ? url.href : ''; }
  catch { return ''; }
}

function normalizeTeam(value) {
  return {
    slug: String(value?.slug || '').trim(),
    name: String(value?.name || '').trim(),
    longName: String(value?.longName || value?.name || '').trim(),
    aliases: Array.isArray(value?.aliases)
      ? value.aliases.map((item) => String(item || '').trim()).filter(Boolean)
      : [],
    logo: String(value?.logo || '').trim(),
  };
}

function normalizeRecord(value) {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const wins = integerOrNull(value.wins);
  const losses = integerOrNull(value.losses);
  const ties = integerOrNull(value.ties) || 0;
  const label = String(value.label || '').trim();

  if (wins === null && losses === null && !label) {
    return null;
  }

  return { wins, losses, ties, label };
}

function normalizeSummary(value) {
  return {
    conferenceCount: integerOrNull(value?.conferenceCount),
    teamCount: integerOrNull(value?.teamCount),
    rowsWithConferenceRecords: integerOrNull(value?.rowsWithConferenceRecords),
    rowsWithOverallRecords: integerOrNull(value?.rowsWithOverallRecords),
    schoolsMatched: integerOrNull(value?.schoolsMatched),
  };
}

function integerOrNull(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const number = Number(value);
  return Number.isInteger(number) ? number : null;
}

function numberOrNull(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

async function readJson(response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}
